import { useQuery } from '@apollo/client'
import { Clear } from '@mui/icons-material'
import {
    Box,
    Stack,
    Typography,
    type AutocompleteRenderGetTagProps,
} from '@mui/material'
import { type FC } from 'react'
import { GetCompaniesById } from 'candidate/graphql/generatedProfile.gql'
import {
    GetIndustryOptions,
    GetSectorOptions,
} from 'recruiter/graphql/companies.gql'
import OfficeLocationsAutocompleteMultiple from 'shared/components/OfficeLocationsAutocomplete/OfficeLocationsAutocompleteMultiple'
import { GetTitleOptions } from 'shared/graphql/titles.gql'
import { type CandidateFilterState } from 'shared/hooks/candidateSearchUrlState'
import {
    FilterWithTimeFrame,
    CompanyAutocomplete,
    CompanyFilterSummary,
    TimeFrameControls,
    AnalystClassYearFilter,
} from './CandidateMinimalFilters'
import { FilterAutocomplete, FilterWrapper } from './FilterPrimitives'
import { minimalInputSx, minimalTagSx } from './minimalFilterStyles'

const locationTagSx = minimalTagSx(false)

type ExcludableOption = { id: string; value: string; excluded: boolean }

const IndustryAutocomplete: FC<{
    value: string[]
    valueNot?: string[]
    onChange: (ids: string[], idsNot?: string[]) => void
}> = ({ value, valueNot = [], onChange }) => {
    const { data, loading } = useQuery(GetIndustryOptions)
    const rawOptions = data?.industries ?? []

    const options: ExcludableOption[] = rawOptions.map((o) => ({
        ...o,
        excluded: false,
    }))

    const allSelected: ExcludableOption[] = [
        ...rawOptions
            .filter((o) => value.includes(o.value))
            .map((o) => ({ ...o, excluded: false })),
        ...rawOptions
            .filter((o) => valueNot.includes(o.value))
            .map((o) => ({ ...o, excluded: true })),
    ]

    const handleTagClick = (opt: ExcludableOption) => {
        if (!opt.excluded) {
            onChange(
                value.filter((v) => v !== opt.value),
                [...valueNot, opt.value]
            )
        } else {
            onChange(
                [...value, opt.value],
                valueNot.filter((v) => v !== opt.value)
            )
        }
    }

    return (
        <FilterAutocomplete
            loading={loading}
            options={options}
            value={allSelected}
            getOptionLabel={(o) => o.value}
            getTagLabel={(o) => o.value}
            isExcluded={(o) => o.excluded}
            onToggleExclude={handleTagClick}
            getOptionKey={(o) => o.id}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            onChange={(_, newVal) => {
                const include = newVal
                    .filter((v) => !v.excluded)
                    .map((v) => v.value)
                const exclude = newVal
                    .filter((v) => v.excluded)
                    .map((v) => v.value)
                onChange(include, exclude)
            }}
        />
    )
}

const NO_SECTORS_SENTINEL = 'NO_SECTORS'
const NO_SECTORS_OPTION = {
    id: NO_SECTORS_SENTINEL,
    value: 'No sectors listed',
}

const toSectorFilterValue = (o: { id: string; value: string }) =>
    o.id === NO_SECTORS_SENTINEL ? NO_SECTORS_SENTINEL : o.value

const SectorAutocomplete: FC<{
    value: string[]
    valueNot?: string[]
    onChange: (ids: string[], idsNot?: string[]) => void
}> = ({ value, valueNot = [], onChange }) => {
    const { data, loading } = useQuery(GetSectorOptions)
    const rawOptions = data?.sectors ?? []
    const allRawOptions = [NO_SECTORS_OPTION, ...rawOptions]

    const options: ExcludableOption[] = allRawOptions.map((o) => ({
        ...o,
        excluded: false,
    }))

    const matchesValue = (o: { id: string; value: string }, list: string[]) =>
        list.includes(o.value) ||
        (o.id === NO_SECTORS_SENTINEL && list.includes(NO_SECTORS_SENTINEL))

    const allSelected: ExcludableOption[] = [
        ...allRawOptions
            .filter((o) => matchesValue(o, value))
            .map((o) => ({ ...o, excluded: false })),
        ...allRawOptions
            .filter((o) => matchesValue(o, valueNot))
            .map((o) => ({ ...o, excluded: true })),
    ]

    const handleTagClick = (opt: ExcludableOption) => {
        const filterVal = toSectorFilterValue(opt)
        if (!opt.excluded) {
            onChange(
                value.filter((v) => v !== filterVal),
                [...valueNot, filterVal]
            )
        } else {
            onChange(
                [...value, filterVal],
                valueNot.filter((v) => v !== filterVal)
            )
        }
    }

    return (
        <FilterAutocomplete
            loading={loading}
            options={options}
            value={allSelected}
            getOptionLabel={(o) => o.value}
            getTagLabel={(o) => o.value}
            isExcluded={(o) => o.excluded}
            onToggleExclude={handleTagClick}
            getOptionKey={(o) => o.id}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            onChange={(_, newVal) => {
                const include = newVal
                    .filter((v) => !v.excluded)
                    .map(toSectorFilterValue)
                const exclude = newVal
                    .filter((v) => v.excluded)
                    .map(toSectorFilterValue)
                onChange(include, exclude)
            }}
        />
    )
}

const PositionAutocomplete: FC<{
    value: string[]
    onChange: (ids: string[]) => void
}> = ({ value, onChange }) => {
    const { data, loading } = useQuery(GetTitleOptions)
    const options = data?.titles ?? []
    const selected = options.filter((o) => value.includes(o.value))

    return (
        <FilterAutocomplete
            loading={loading}
            options={options}
            value={selected}
            getOptionLabel={(o) => o.value}
            getTagLabel={(o) => o.value}
            getOptionKey={(o) => o.id}
            onChange={(_, newVal) => onChange(newVal.map((o) => o.value))}
        />
    )
}

const isGroupMarker = (v: string) => v.startsWith(', ')

const getStateFromLocation = (locationValue: string): string => {
    const parts = locationValue.split(', ')
    return parts.length > 1 ? parts[parts.length - 1] : 'Other'
}

const LocationTextAutocomplete: FC<{
    value: string[]
    onChange: (values: string[]) => void
}> = ({ value: locationValues, onChange }) => {
    const selectedGroups = locationValues.filter(isGroupMarker)
    const selectedIndividuals = locationValues.filter((v) => !isGroupMarker(v))

    const selectedLocations = [
        ...selectedGroups.map((g) => ({ id: g, value: g })),
        ...selectedIndividuals.map((v) => ({ id: v, value: v })),
    ]

    const handleGroupClick = (state: string) => {
        const groupMarker = `, ${state}`
        if (selectedGroups.includes(groupMarker)) {
            onChange(locationValues.filter((v) => v !== groupMarker))
        } else {
            const withoutStateIndividuals = locationValues.filter(
                (v) => isGroupMarker(v) || getStateFromLocation(v) !== state
            )
            onChange([...withoutStateIndividuals, groupMarker])
        }
    }

    const handleChange = (
        _: unknown,
        newValue: Array<{ id: string; value: string }>
    ) => {
        const newGroups = newValue
            .filter((v) => isGroupMarker(v.value))
            .map((v) => v.value)
        const newIndividuals = newValue
            .filter((v) => !isGroupMarker(v.value))
            .map((v) => v.value)

        const newlyAddedIndividuals = newIndividuals.filter(
            (v) => !selectedIndividuals.includes(v)
        )

        let updatedGroups = [...newGroups]
        for (const added of newlyAddedIndividuals) {
            const state = getStateFromLocation(added)
            const groupMarker = `, ${state}`
            if (updatedGroups.includes(groupMarker)) {
                updatedGroups = updatedGroups.filter((g) => g !== groupMarker)
            }
        }

        onChange([...updatedGroups, ...newIndividuals])
    }

    const renderLocationTags = (
        tagValues: Array<{ id: string; value: string }>,
        getTagProps: AutocompleteRenderGetTagProps
    ) =>
        tagValues.length > 0 ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, py: 0.5 }}>
                {tagValues.map((option, index) => {
                    const isGroup = isGroupMarker(option.value)
                    const displayValue = isGroup
                        ? option.value.slice(2)
                        : option.value
                    const { key, onDelete, ...tagProps } = getTagProps({
                        index,
                    })

                    return (
                        <Typography
                            key={option.id}
                            {...tagProps}
                            onClick={
                                isGroup
                                    ? () => handleGroupClick(displayValue)
                                    : onDelete
                            }
                            component="span"
                            sx={locationTagSx}
                        >
                            {displayValue}
                            <Clear sx={{ fontSize: '0.625rem' }} />
                        </Typography>
                    )
                })}
            </Box>
        ) : null

    const transformOptions = (
        options: Array<{ id: string; value: string }>
    ) => {
        const result: Array<{ id: string; value: string }> = []
        let currentState: string | null = null

        for (const opt of options) {
            const state = getStateFromLocation(opt.value)
            if (state !== currentState) {
                // Skip "Other" group header - it's not a meaningful geographic selector
                if (state !== 'Other') {
                    result.push({ id: `header-${state}`, value: `, ${state}` })
                }
                currentState = state
            }
            result.push(opt)
        }
        return result
    }

    return (
        <OfficeLocationsAutocompleteMultiple
            size="small"
            label=""
            onChange={handleChange}
            getOptionLabel={(option) =>
                isGroupMarker(option.value)
                    ? option.value.slice(2)
                    : option.value
            }
            placeholder="Type to search..."
            value={selectedLocations}
            sx={minimalInputSx}
            renderTags={renderLocationTags}
            transformOptions={transformOptions}
            isOptionEqualToValue={(option, value) =>
                option.value === value.value
            }
            renderOption={({ key, ...optionProps }, option) => {
                const isHeader = isGroupMarker(option.value)
                const displayValue = isHeader
                    ? option.value.slice(2)
                    : option.value
                const isSelected = isHeader
                    ? selectedGroups.includes(option.value)
                    : selectedIndividuals.includes(option.value)

                return (
                    <Box
                        component="li"
                        key={option.id}
                        {...optionProps}
                        onClick={
                            isHeader
                                ? (e) => {
                                      e.stopPropagation()
                                      handleGroupClick(displayValue)
                                  }
                                : optionProps.onClick
                        }
                        sx={{
                            fontSize: isHeader ? '0.6875rem' : '0.75rem',
                            fontWeight: isHeader ? 600 : 400,
                            py: isHeader ? 0.25 : 0.5,
                            pl: isHeader ? 1 : 3,
                            letterSpacing: isHeader ? '0.05em' : 'normal',
                            bgcolor: isSelected
                                ? 'primary.50'
                                : isHeader
                                  ? 'grey.50'
                                  : 'inherit',
                            borderLeft: isSelected ? '3px solid' : 'none',
                            borderLeftColor: 'primary.main',
                            minHeight: 'unset',
                        }}
                    >
                        {displayValue}
                    </Box>
                )
            }}
        />
    )
}

interface ExperienceFilterSectionProps {
    filters: CandidateFilterState
    updateFilters: (patch: Partial<CandidateFilterState>) => void
}

export const ExperienceFilterSection: FC<ExperienceFilterSectionProps> = ({
    filters,
    updateFilters,
}) => {
    const { data: companyData } = useQuery(GetCompaniesById, {
        variables: { ids: filters.companyIds },
        skip: filters.companyIds.length === 0,
    })
    const companyNames = filters.companyIds.map((id) => {
        const found = companyData?.companiesById.find((c) => c.id === id)
        return found?.value ?? ''
    })

    return (
        <Stack gap={2.5} sx={{ p: 2 }}>
            <FilterWrapper label="Company">
                <Stack gap={1.5}>
                    <CompanyAutocomplete
                        value={filters.companyIds}
                        onChange={(companyIds) =>
                            updateFilters({
                                companyIds,
                                ...(companyIds.length === 0 && {
                                    companyTimeFrame: undefined,
                                    companyRangeGte: undefined,
                                    companyRangeLte: undefined,
                                    companyAdvancedStartGte: undefined,
                                    companyAdvancedStartLte: undefined,
                                    companyAdvancedEndGte: undefined,
                                    companyAdvancedEndLte: undefined,
                                }),
                            })
                        }
                    />
                    {filters.companyIds.length > 0 && (
                        <TimeFrameControls
                            timeFrame={filters.companyTimeFrame}
                            rangeGte={filters.companyRangeGte}
                            rangeLte={filters.companyRangeLte}
                            onChangeTimeFrame={(companyTimeFrame) =>
                                updateFilters({ companyTimeFrame })
                            }
                            onChangeRange={(companyRangeGte, companyRangeLte) =>
                                updateFilters({
                                    companyRangeGte,
                                    companyRangeLte,
                                })
                            }
                            advancedStartGte={filters.companyAdvancedStartGte}
                            advancedStartLte={filters.companyAdvancedStartLte}
                            advancedEndGte={filters.companyAdvancedEndGte}
                            advancedEndLte={filters.companyAdvancedEndLte}
                            onChangeAdvancedStart={(
                                companyAdvancedStartGte,
                                companyAdvancedStartLte
                            ) =>
                                updateFilters({
                                    companyAdvancedStartGte,
                                    companyAdvancedStartLte,
                                })
                            }
                            onChangeAdvancedEnd={(
                                companyAdvancedEndGte,
                                companyAdvancedEndLte
                            ) =>
                                updateFilters({
                                    companyAdvancedEndGte,
                                    companyAdvancedEndLte,
                                })
                            }
                        />
                    )}
                    <CompanyFilterSummary
                        companyNames={companyNames}
                        timeFrame={filters.companyTimeFrame}
                        rangeGte={filters.companyRangeGte}
                        rangeLte={filters.companyRangeLte}
                        advancedStartGte={filters.companyAdvancedStartGte}
                        advancedStartLte={filters.companyAdvancedStartLte}
                        advancedEndGte={filters.companyAdvancedEndGte}
                        advancedEndLte={filters.companyAdvancedEndLte}
                    />
                </Stack>
            </FilterWrapper>
            <FilterWithTimeFrame
                label="Position"
                ids={filters.positionTitles}
                timeFrame={filters.positionTimeFrame}
                rangeGte={filters.positionRangeGte}
                rangeLte={filters.positionRangeLte}
                onChangeIds={(positionTitles) =>
                    updateFilters({
                        positionTitles,
                        ...(positionTitles.length === 0 && {
                            positionTimeFrame: undefined,
                            positionRangeGte: undefined,
                            positionRangeLte: undefined,
                        }),
                    })
                }
                onChangeTimeFrame={(positionTimeFrame) =>
                    updateFilters({
                        positionTimeFrame,
                        ...(positionTimeFrame !== 'range' && {
                            positionRangeGte: undefined,
                            positionRangeLte: undefined,
                        }),
                    })
                }
                onChangeRange={(positionRangeGte, positionRangeLte) =>
                    updateFilters({ positionRangeGte, positionRangeLte })
                }
                filterComponent={PositionAutocomplete}
            />
            <FilterWithTimeFrame
                label="Industry"
                ids={filters.industryNames}
                idsNot={filters.industryNamesNot}
                timeFrame={filters.industryTimeFrame}
                rangeGte={filters.industryRangeGte}
                rangeLte={filters.industryRangeLte}
                onChangeIds={(industryNames, industryNamesNot) =>
                    updateFilters({
                        industryNames,
                        industryNamesNot:
                            industryNamesNot ?? filters.industryNamesNot,
                        ...(industryNames.length === 0 &&
                            (industryNamesNot ?? filters.industryNamesNot)
                                .length === 0 && {
                                industryTimeFrame: undefined,
                                industryRangeGte: undefined,
                                industryRangeLte: undefined,
                                industryTopTierOnly: false,
                            }),
                    })
                }
                onChangeTimeFrame={(industryTimeFrame) =>
                    updateFilters({
                        industryTimeFrame,
                        ...(industryTimeFrame !== 'range' && {
                            industryRangeGte: undefined,
                            industryRangeLte: undefined,
                        }),
                    })
                }
                onChangeRange={(industryRangeGte, industryRangeLte) =>
                    updateFilters({ industryRangeGte, industryRangeLte })
                }
                filterComponent={IndustryAutocomplete}
                topTierOnly={filters.industryTopTierOnly}
                onChangeTopTierOnly={(industryTopTierOnly) =>
                    updateFilters({ industryTopTierOnly })
                }
            />
            <FilterWithTimeFrame
                label="Sector"
                ids={filters.sectorNames}
                idsNot={filters.sectorNamesNot}
                timeFrame={filters.sectorTimeFrame}
                rangeGte={filters.sectorRangeGte}
                rangeLte={filters.sectorRangeLte}
                onChangeIds={(sectorNames, sectorNamesNot) =>
                    updateFilters({
                        sectorNames,
                        sectorNamesNot:
                            sectorNamesNot ?? filters.sectorNamesNot,
                        ...(sectorNames.length === 0 &&
                            (sectorNamesNot ?? filters.sectorNamesNot)
                                .length === 0 && {
                                sectorTimeFrame: undefined,
                                sectorRangeGte: undefined,
                                sectorRangeLte: undefined,
                                sectorTopTierOnly: false,
                            }),
                    })
                }
                onChangeTimeFrame={(sectorTimeFrame) =>
                    updateFilters({
                        sectorTimeFrame,
                        ...(sectorTimeFrame !== 'range' && {
                            sectorRangeGte: undefined,
                            sectorRangeLte: undefined,
                        }),
                    })
                }
                onChangeRange={(sectorRangeGte, sectorRangeLte) =>
                    updateFilters({ sectorRangeGte, sectorRangeLte })
                }
                filterComponent={SectorAutocomplete}
                topTierOnly={filters.sectorTopTierOnly}
                onChangeTopTierOnly={(sectorTopTierOnly) =>
                    updateFilters({ sectorTopTierOnly })
                }
            />
            <FilterWithTimeFrame
                label="Location"
                ids={filters.locationText}
                timeFrame={filters.locationTextTimeFrame}
                rangeGte={filters.locationTextRangeGte}
                rangeLte={filters.locationTextRangeLte}
                onChangeIds={(locationText) =>
                    updateFilters({
                        locationText,
                        ...(locationText.length === 0 && {
                            locationTextTimeFrame: undefined,
                            locationTextRangeGte: undefined,
                            locationTextRangeLte: undefined,
                        }),
                    })
                }
                onChangeTimeFrame={(locationTextTimeFrame) =>
                    updateFilters({
                        locationTextTimeFrame,
                        ...(locationTextTimeFrame !== 'range' && {
                            locationTextRangeGte: undefined,
                            locationTextRangeLte: undefined,
                        }),
                    })
                }
                onChangeRange={(locationTextRangeGte, locationTextRangeLte) =>
                    updateFilters({
                        locationTextRangeGte,
                        locationTextRangeLte,
                    })
                }
                filterComponent={LocationTextAutocomplete}
            />
            <AnalystClassYearFilter
                gte={filters.analystClassYearGte}
                lte={filters.analystClassYearLte}
                onChange={(analystClassYearGte, analystClassYearLte) =>
                    updateFilters({
                        analystClassYearGte,
                        analystClassYearLte,
                    })
                }
            />
        </Stack>
    )
}
